import * as THREE from 'three'
import React, { useMemo } from 'react'
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../../../redux/reducers/reducer';
import { controls } from '../../../../../../../../redux/reducers/common/eventReducer';

import { CSG } from '../../../../../../../../assets/libs/csg-lib';
import { delModel, extrudeSettings, roofPattern, textureAnisotropy} from '../../../../../../../../utils/Functions';
import useMouseEvents from '../../../../../../../../hooks/useMouseEvents';
import { MaterialInnerWall, MaterialOuterWallNew, MaterialWainScotWallNew } from '../../../../../../CustomMaterial';
import { getSimilarTexture, textureAppliedFor } from '../../../../../../../../redux/reducers/utils';

const WallModel = () => {
    const { gl } = useThree ();

    const wallThickness = 0.005;
    const cbFrontBackWallOffset = 0.16;

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement !== undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false    

    const textureObj = useMemo(() => {
        let similarSidingTextures = getSimilarTexture(params.wallTexture)
        let similarWainscotTextures = getSimilarTexture(params.wainscotTexture)

        const texture = const_var.wallTextureObj?.[similarSidingTextures]?.clone();
        const wainscotTexture = const_var.wallTextureObj?.[similarWainscotTextures]?.clone();

        const metalTexture = const_var.wallTextureObj?.["vertical"]?.clone();
        const lightTexture = const_var.wallTextureObj?.["vertical_light"]?.clone();
        
        if(metalTexture){
            if (params.wallOrientation == "vertical") metalTexture.rotation = Math.PI/-2;
            else metalTexture.rotation = 0;
        }
        if(lightTexture) lightTexture.rotation = metalTexture.rotation;
        if(texture && const_var.selSidingOption === "metal") texture.rotation = metalTexture.rotation
        if(wainscotTexture && (params.wainscotTexture === "none" || params.wainscotTexture === "vertical")) wainscotTexture.rotation = metalTexture.rotation

        return {"texture":texture,"wainscotTexture" : wainscotTexture,"metalTexture":metalTexture,"lightTexture":lightTexture};
    }, [params.p_v_w, params.wallTexture, const_var.wallTextureObj, const_var.selSidingOption, params.wallOrientation,params.wainscotTexture]);

    const texture = useMemo(() => {
        if(const_var.metalTexture){
            let texture = const_var.metalTexture.clone();
            textureAnisotropy(gl, texture,1.3, 1.3, (params.p_v_w != true) ? 0 :  Math.PI/-2);    
            return texture
        }else{
            return null
        }
    }, [params.p_v_w,params.wallTexture,const_var.wallTextureObj[params.wallTexture]])
    
    const sideWallModelObj = useMemo(() => {
        let outerModel;
        let innerModel;
        let wainscotModel;

        let verticalHorizontalPosS = {"x": params.p_w / 2  + params.leanR_p_w,"y" : ((params.p_w_c_n && (params.p_b_c_b_r === 'Close' || params.add_storage_right)) ? 2.935 : 0), "z" : params.leanR_p_d / 2}
        let verticalHorizontalRotS = {"x":0,"y" : Math.PI,"z" : Math.PI / 2}
        let patternLengthS = params.leanR_p_h - 0.1 + (params.p_r_s === 1 && 0.15) + ((params.p_w_c_n && (params.p_b_c_b_r === 'Close' || params.add_storage_right)) ? -2.935 : 0)
        let patternDepthS = params.leanR_p_d

        let lengthPatternS = 3
        let depthPatternS = patternDepthS

        //************************* */ inner wall geometry        
        const sideWallModel = new THREE.Shape();
        sideWallModel.moveTo(-(params.leanR_p_d / 2 + cbFrontBackWallOffset), 0);
        sideWallModel.lineTo((-params.leanR_p_d / 2 + parseInt(params.add_storage_right) + 0.02), 0);
        sideWallModel.lineTo((-params.leanR_p_d / 2 + parseInt(params.add_storage_right) + 0.02), params.leanR_p_h + (params.p_r_s === 1 && 0.1));
        sideWallModel.lineTo(-(params.leanR_p_d / 2 + cbFrontBackWallOffset), params.leanR_p_h + (params.p_r_s === 1 && 0.1));
        sideWallModel.closePath();

        const meshSideWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(sideWallModel, extrudeSettings(0.005)));
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('rightLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
    
        let meshSideWallModel_Temp = meshSideWallModel.clone();
        meshSideWallModel_Temp.rotateY(-Math.PI / 2);
        meshSideWallModel_Temp.position.set(params.p_w / 2 + params.leanR_p_w - 0.03, 0, 0);
        meshSideWallModel_Temp.updateMatrixWorld();
    
        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.rotateY(Math.PI / 2)
        meshSideDelModel_Temp.position.set(params.p_w / 2 + params.leanR_p_w -0.5, 0, -params.leanR_p_pos - params.rightLeanStoragePosition);
        meshSideDelModel_Temp.updateMatrixWorld();
    
        const meshSideWallModel_CSG = CSG.fromMesh(meshSideWallModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
    
        const sideWallModel_CSG = meshSideWallModel_CSG.subtract(meshSideDelModel_CSG);
        const CSGSideWallModel = CSG.toMesh(
            sideWallModel_CSG,
            new THREE.Matrix4()
        )

        innerModel = CSGSideWallModel.geometry;

        //************************* */ outer wall geometry

        const meshSideWallModelPattern = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthS, isPattern), extrudeSettings(patternDepthS)));

        const meshWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(sideWallModel, extrudeSettings(1)))
        
        let meshWallModel_TempS = meshSideWallModelPattern.clone();
        meshWallModel_TempS.position.set(verticalHorizontalPosS.x,verticalHorizontalPosS.y,verticalHorizontalPosS.z);
        meshWallModel_TempS.rotation.set(verticalHorizontalRotS.x,verticalHorizontalRotS.y,verticalHorizontalRotS.z);
        meshWallModel_TempS.updateMatrixWorld();
    
    
        let meshWallDelModel_TempS = meshWallDelModel.clone();
        meshWallDelModel_TempS.position.set(params.p_w / 2 + params.leanR_p_w + 0.5 , 0, 0);
        meshWallDelModel_TempS.rotation.set(0,Math.PI / -2,0)
        meshWallDelModel_TempS.updateMatrixWorld();
    
        const meshWallModel_CSGS = CSG.fromMesh(meshWallModel_TempS);
        const meshWallDelModel_CSGS = CSG.fromMesh(meshWallDelModel_TempS);
    
    
        const wallModel_CSGS = meshWallModel_CSGS.subtract(meshSideDelModel_CSG);
        const meshWallDel_CSGS = wallModel_CSGS.intersect(meshWallDelModel_CSGS);
        const CSGWallModelS = CSG.toMesh(meshWallDel_CSGS,new THREE.Matrix4());

        outerModel = CSGWallModelS.geometry

        //************************* */ wainscot wall geometry
        let wainscotVerticalHorizontalPosS = {"x": params.p_w / 2  + params.leanR_p_w,"y" : 0, "z" : params.leanR_p_d / 2}
        let wainscotVerticalHorizontalRotS = {"x":0,"y" : Math.PI,"z" : Math.PI / 2}

        const meshSideWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternS, isPattern), extrudeSettings(depthPatternS)))
        let meshSideWainscotModel_Temp = meshSideWainscotModel.clone();
        meshSideWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosS.x,wainscotVerticalHorizontalPosS.y,wainscotVerticalHorizontalPosS.z);
        meshSideWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotS.x,wainscotVerticalHorizontalRotS.y,wainscotVerticalHorizontalRotS.z);
        meshSideWainscotModel_Temp.updateMatrixWorld();

        const meshSideWainscotModel_CSG = CSG.fromMesh(meshSideWainscotModel_Temp);

        const SideWainscotModel_CSG = meshSideWainscotModel_CSG.subtract(meshSideDelModel_CSG);
        const SideWainscotModelCSG = SideWainscotModel_CSG.intersect(meshWallDelModel_CSGS);
        const CSGSideWainscotModel = CSG.toMesh(
            SideWainscotModelCSG,
            new THREE.Matrix4()
            )

        wainscotModel = CSGSideWainscotModel.geometry;

        return {outerModel, innerModel, wainscotModel}
    
    }, [params.wallTexture,params.p_w, params.leanR_p_h, params.p_r_s,params.leanR_p_d, params.rightLeanStoragePosition, params.add_storage_right, params.add_storage_check_right,params.add_right_front_lean_porch, params.add_right_back_lean_porch, params.p_w_c_n, params.leanR_p_w, params.p_v_w, const_var.newDoorsArray,params.p_b_c_b_r, isPattern])

    

    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "rightlean" && params.activeWall === "R_L_F_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);

    return (
        <group name='right_lean_side_wall' {...eventHandlers}>
            {sideWallModelObj?.outerModel &&
                <mesh name="R_L_R_S_W_outer" geometry={sideWallModelObj?.outerModel} renderOrder={1}>
                    <MaterialOuterWallNew isPattern={isPattern} visibleSection={"rightlean"} />
                </mesh>
            }
            {/* Side Wainscot */}
            {params.p_w_c_n && sideWallModelObj?.wainscotModel &&
                <mesh name="R_L_R_S_W_wainscot" geometry={sideWallModelObj?.wainscotModel} position={[0.005,0,0]} renderOrder={1}>
                    <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"rightlean"} />
                </mesh>
            }
            {sideWallModelObj && sideWallModelObj?.innerModel && 
                <mesh name="R_L_R_S_W_inner" geometry={sideWallModelObj?.innerModel} position={[-wallThickness - 0.01, 0, 0]}>
                    <MaterialInnerWall texture={texture} visibleSection={"rightLean"} />
                </mesh>
            }
        </group>

    )
}

export default WallModel